/**
 * Typed fetch wrapper around the Tinsmith API.
 *
 * Keeps every API call in one place so:
 *   - the base URL is configurable via VITE_API_BASE_URL
 *   - error envelopes ({error: {kind, message, retryable}}) are translated to a single ApiError class
 *   - DTOs are typed end-to-end without depending on the API package directly
 *
 * Decimal-string conventions: any field documented as "Decimal as string" in
 * the API's OpenAPI spec (qtys, prices) arrives here as a string. UI code that
 * does arithmetic should pump those through decimal.js — never through Number.
 */

import { currentAccessToken, useAuth } from './auth-store.js';
import { attemptSessionRestore } from './session-keeper.js';

const DEFAULT_BASE = '/api';
const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? DEFAULT_BASE;

/**
 * Paths that don't need (or shouldn't carry) a Bearer token — the dev IDP's
 * own endpoints + the API liveness probe. Login itself can't carry a token
 * because we don't have one yet; healthz is intentionally @Public.
 */
const PUBLIC_PATH_PREFIXES = ['/v1/dev-auth/', '/healthz', '/readyz'];

function isPublicPath(path: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((p) => path.startsWith(p));
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** Spire / domain error kind from the response envelope, when present. */
    readonly kind: string | null,
    message: string,
    readonly retryable: boolean = false,
    /** Raw parsed response body. Surfaced for callers who need to read a
     * domain object on a non-2xx (e.g. 409 { code, activeJobSpireId, ... }
     * on the field-time-entry start endpoint). Undefined for
     * transport-level errors where no body was read. */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── DTOs (mirror apps/api/src/modules/inventory/inventory.dto.ts) ────────

export interface InventoryItemSummary {
  spireId: number;
  partNo: string;
  whseCode: string;
  description: string | null;
  uom: string | null;
  status: 'active' | 'inactive' | 'obsolete' | 'unknown';
  /** Decimal as string. */
  onHandQty: string;
  committedQty: string;
  onPurchaseQty: string;
  availableQty: string;
  defaultSellPrice: string;
  modifiedAt: string | null;
}

export interface InventoryUnitOfMeasure {
  code: string;
  description: string | null;
  quantityFactor: string;
  buyUom: boolean;
  sellUom: boolean;
}

export interface InventoryItem extends InventoryItemSummary {
  extendedDescription: string | null;
  /** 20-rung sell-price ladder (index 0 = sellLevel 1). */
  sellPrices: string[];
  pricingByUom: Record<string, string[]>;
  unitOfMeasures: InventoryUnitOfMeasure[];
}

// ─── Customer DTOs (mirror apps/api/src/modules/customers/customers.dto.ts) ─

export interface CustomerAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  provState: string | null;
  postalCode: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
}

export interface CustomerSummary {
  spireId: number;
  customerNo: string;
  name: string;
  hold: boolean;
  status: 'active' | 'inactive' | 'hold' | 'unknown';
  sellLevel: number;
  defaultWarehouse: string | null;
  creditLimit: string | null;
  creditBalance: string;
  openOrdersTotal: string;
  thisYearSales: string;
  lastInvoiceDate: string | null;
  address: CustomerAddress;
  /** Free-text Spire user-defined field 1 — typically internal notes / specialty. */
  userDef1: string | null;
  /** Free-text Spire user-defined field 2 — commonly the trade category. */
  userDef2: string | null;
  modifiedAt: string | null;
}

export interface CustomerContact {
  name: string | null;
  email: string | null;
  phone: string | null;
  contactType: string | null;
}

/** A ship-to (service address) on the customer, with its own contacts. */
export interface CustomerShipTo extends CustomerAddress {
  shipId: string | null;
  /** Name on the ship-to — usually the person on site. */
  name: string | null;
  line3: string | null;
  line4: string | null;
  fax: string | null;
  defaultWarehouse: string | null;
  sellLevel: number;
  salesperson: string | null;
  territory: string | null;
  isDefault: boolean;
  contacts: CustomerContact[];
}

export interface Customer extends CustomerSummary {
  discount: string;
  paymentTermsCode: string | null;
  paymentTermsDescription: string | null;
  primaryContact: CustomerContact | null;
  currency: string | null;
  creditApprovedBy: string | null;
  creditApprovedDate: string | null;
  reference: string | null;
  defaultShipTo: string | null;
  website: string | null;
  salesperson: string | null;
  salespersonName: string | null;
  territory: string | null;
  createdAt: string | null;
  /** Named contacts on the billing address. */
  contacts: CustomerContact[];
  /** Every ship-to on file, default first. */
  shipTos: CustomerShipTo[];
}

// ─── Sales-order DTOs (mirror apps/api/src/modules/sales-orders/sales-orders.dto.ts) ─

export interface SalesOrderAddress {
  name: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  provState: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface SalesOrderCustomerRef {
  /** Null when the order has no linked customer (cash sale / legacy import). */
  spireId: number | null;
  /** '' when unlinked; name falls back to the billing-address name server-side. */
  customerNo: string;
  name: string;
  hold: boolean;
  invoiceType: string | null;
  /** Free-text user-defined field 2 — commonly the trade category. */
  userDef2: string | null;
}

export interface SalesOrderSummary {
  spireId: number;
  orderNo: string;
  invoiceNo: string | null;
  status: 'open' | 'invoiced' | 'cancelled' | 'unknown';
  type: 'order' | 'quote' | 'unknown';
  hold: boolean;
  /** Spire workflow phase code (Edit → Phases); null when unset. */
  phaseId: string | null;
  customer: SalesOrderCustomerRef;
  orderDate: string | null;
  requiredDate: string | null;
  shipDate: string | null;
  subtotal: string;
  total: string;
  freight: string;
  totalDiscount: string;
  grossProfit: string;
  customerPO: string | null;
  billingAddress: SalesOrderAddress;
  shippingAddress: SalesOrderAddress;
  modifiedAt: string | null;
  /** Per-order contact fields. Populated when the search was called with
   * `includeUdf=true` AND the order had FOP_TECH set (same trigger as the
   * UDF detail-fetch). Best-effort — treat as nullable. */
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  /** Spire UDF object on the order header. Company-specific keys; see CLAUDE.md UDF rules. */
  udf: Record<string, string>;
}

export interface SalesOrderTax {
  code: number | null;
  name: string | null;
  rate: string;
  total: string;
}

export interface SalesOrderSerial {
  serialNumber: string;
  sellPrice: string;
  unitCost: string;
}

export interface SalesOrderLineItem {
  spireId: number;
  sequence: number;
  inventory: { spireId: number; whse: string; partNo: string; description: string | null } | null;
  whseCode: string;
  partNo: string;
  description: string | null;
  comment: string | null;
  orderQty: string;
  committedQty: string;
  backorderQty: string;
  sellMeasure: string | null;
  unitPrice: string;
  extendedPriceCommitted: string;
  /** Line amount to display: ordered until invoiced, committed after. Absent from pre-2026-09-21 APIs. */
  extendedPrice?: string;
  userPrice: boolean;
  discountPct: string;
  serials: SalesOrderSerial[];
}

export interface SalesOrder extends SalesOrderSummary {
  currencyCode: string;
  currencyRate: string;
  termsCode: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  taxes: SalesOrderTax[];
  items: SalesOrderLineItem[];
  /** Spire UDF keys — company-specific (e.g. "SALES TYP" carries trade). */
  udf: Record<string, string>;
}

// ─── Public client surface ────────────────────────────────────────────────

export interface InventorySearchParams {
  whse?: string;
  /** Free-text: part-number fragments + description, any case. For search boxes. */
  q?: string;
  /** A KNOWN part number (scan, add-to-quote) — exact match, falling back to a substring match. */
  partNo?: string;
  groupNo?: string;
  limit?: number;
}

export interface CustomerSearchParams {
  q?: string;
  /** EXACT customer number — use to resolve a known customer (customerNo is a substring match). */
  customerNoExact?: string;
  customerNo?: string;
  hold?: boolean;
  status?: string;
  limit?: number;
}

export interface SalesOrderSearchParams {
  status?: string;
  type?: string;
  customerNo?: string;
  orderNo?: string;
  /** Filter to orders whose udf.FOP_TECH equals this Tech.id (field "My day"). */
  assignedToTechId?: string;
  limit?: number;
  /** When true, server-side enriches each summary with the order's UDF object. Used by the dispatch board. */
  includeUdf?: boolean;
}

export interface CreateSalesOrderLineBody {
  whse: string;
  partNo: string;
  description?: string;
  comment?: string;
  orderQty: string;
  unitPrice?: string;
  sellMeasure?: string;
}

/** One-off service address on a new order (field-created jobs). */
export interface CreateOrderShipToBody {
  name: string;
  line1?: string;
  line2?: string;
  city?: string;
  provState?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
}

export interface CreateSalesOrderBody {
  customerNo: string;
  type?: 'W' | 'Q';
  hold?: boolean;
  orderDate?: string;
  requiredDate?: string;
  customerPO?: string;
  referenceNo?: string;
  /** Header UDF on create. A field-created job sends its fop_* assignment here. */
  udf?: Record<string, string>;
  shippingAddress?: CreateOrderShipToBody;
  /** May be empty only for a field-created job (udf.fop_tech set). */
  items: CreateSalesOrderLineBody[];
}

/** Field-created jobs ("Enercare mode") — see Admin → Field jobs. */
export interface FieldJobsSettings {
  enabled: boolean;
  billToCustomerNo: string | null;
  billToCustomerName: string | null;
  typeUdfKey: string;
  requirePo: boolean;
}

/**
 * Partial update for an existing sales order. Verified against spirelive:
 * top-level fields are writable; the wire DTO matches apps/api's
 * UpdateSalesOrderInputDto.
 */
export interface UpdateSalesOrderBody {
  requiredDate?: string;
  shipDate?: string;
  customerPO?: string;
  referenceNo?: string;
  hold?: boolean;
  fob?: string;
  /** 'W' promotes a quote to a work order; used by dispatch on schedule. */
  type?: 'W' | 'Q';
  /** Patch the UDF object — Spire merges with the existing record. */
  udf?: Record<string, string>;
  /** Override the in-progress guard on an unschedule (after showing the dispatcher the 409 details). */
  force?: boolean;
}

/** 409 body when an unschedule is refused because a tech is on the job or has completed it. */
export interface JobInProgressConflict {
  code: 'JOB_IN_PROGRESS';
  reason: 'active' | 'completed';
  techName: string | null;
  since: string | null;
  orderNo: string;
  message: string;
}

export function isJobInProgressBody(body: unknown): body is JobInProgressConflict {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { code?: unknown }).code === 'JOB_IN_PROGRESS' &&
    ((body as { reason?: unknown }).reason === 'active' || (body as { reason?: unknown }).reason === 'completed')
  );
}

export interface AddLineBody {
  whse: string;
  partNo: string;
  orderQty: string;
  description?: string;
  sellMeasure?: string;
  unitPrice?: string;
  source?: 'field' | 'office';
}

export interface AddLineResponse {
  orderId: number;
  orderNo: string;
  lineId: number;
  sequence: number;
  partNo: string;
}

export interface AttachSignatureBody {
  contentType: 'image/png' | 'image/jpeg';
  /** Raw base64 (no data: prefix). */
  base64: string;
  filename?: string;
  subject?: string;
}

export interface AttachSignatureResponse {
  orderNo: string;
  noteId: number;
  attachmentId: number;
}

export interface AddPhotoBody {
  contentType: 'image/png' | 'image/jpeg';
  base64: string;
  caption?: string;
  filename?: string;
}

export interface AddPhotoResponse {
  orderNo: string;
  /** Spire CRM-note mirror ids — null when the best-effort mirror failed. */
  noteId: number | null;
  attachmentId: number | null;
}

export type JobAttachmentContentType =
  | 'image/png'
  | 'image/jpeg'
  | 'image/webp'
  | 'image/heic'
  | 'application/pdf';

/** A written note Tinsmith put on the order (stored as a Spire CRM note). */
export interface JobNote {
  noteId: number;
  /** 'quote' = sales rep, on the quote; 'office' = office, for the tech; 'field' = a tech's field note. */
  kind: 'quote' | 'office' | 'field';
  body: string;
  createdAt: string | null;
  /** The Tinsmith user who wrote it when known, else Spire's user code. */
  createdBy: string | null;
}

/** A company job category (Admin → Sales types). Mirror apps/api/src/modules/sales-types. */
export interface SalesType {
  /** Stable id; what tech skills store. */
  key: string;
  label: string;
  /** Hex, e.g. "#185FA5". */
  color: string;
  /** Lower-cased Spire values (SALES TYP / userDef2) that map to this type. */
  matchValues: string[];
  isSkill: boolean;
  active: boolean;
  sortOrder: number;
}

export interface CreateSalesTypeBody {
  label: string;
  color: string;
  matchValues?: string[];
  isSkill?: boolean;
}

export type UpdateSalesTypeBody = Partial<Pick<SalesType, 'label' | 'color' | 'matchValues' | 'isSkill' | 'active' | 'sortOrder'>>;

/** A Spire sales-order phase definition (Edit → Phases in Spire desktop). */
export interface SpirePhase {
  spireId: number;
  /** The code an order's phaseId holds, e.g. "READY". */
  phaseId: string;
  description: string | null;
  phaseType: string;
  nextPhaseId: string | null;
}

/** Job phase → Spire phase code; '' = leave Spire's phase alone at that step. */
export type PhaseMap = Record<FieldPhase, string>;

export interface OfficePhaseResult {
  orderNo: string;
  /** Who has the job open right now, if anyone — their clock is untouched. */
  onJob: { techName: string | null; since: string } | null;
}

/** Company scheduling settings (mirror apps/api/src/modules/settings). */
export interface SchedulingSettings {
  /** Hours a freshly dropped job takes on the board. */
  defaultJobHours: number;
  /** First hour of the board day, 0–23. */
  dayStartHour: number;
  /** Hour the board day ends (exclusive), 1–24. */
  dayEndHour: number;
}

/** A file on a job, stored in Tinsmith's object store. */
export interface JobAttachment {
  id: string;
  kind: 'photo' | 'document';
  filename: string;
  contentType: string;
  sizeBytes: number;
  caption: string | null;
  uploadedByName: string | null;
  createdAt: string;
  /** Short-lived signed URL (~15 min) — refetch the list rather than caching it. */
  url: string;
}

export interface UploadJobAttachmentBody {
  contentType: JobAttachmentContentType;
  base64: string;
  filename: string;
  kind?: 'photo' | 'document';
  caption?: string;
}

export interface OrderPhoto {
  noteId: number;
  caption: string;
  createdAt: string | null;
  createdBy: string | null;
}

// ─── Pricing DTOs (mirror apps/api/src/modules/pricing/pricing.dto.ts) ────

export interface ResolvePricingLineRequest {
  whse: string;
  partNo: string;
  /** Decimal as string. */
  orderQty: string;
  sellMeasure?: string;
}

export interface ResolvePricingBody {
  customerNo: string;
  items: ResolvePricingLineRequest[];
}

export interface ResolvedPriceLine {
  whse: string;
  partNo: string;
  orderQty: string;
  unitPrice: string;
  lineTotal: string;
  source: 'matrix' | 'customer_sell_level' | 'item_default';
  sellLevel: number | null;
  description: string | null;
  found: boolean;
}

export interface ResolvePricingResponse {
  customerNo: string;
  customerSellLevel: number;
  lines: ResolvedPriceLine[];
}

// ─── Admin DTOs (mirror apps/api/src/modules/admin/admin.dto.ts) ──────────

export interface SpireHealth {
  reachable: boolean;
  latencyMs: number;
  breakerState: 'closed' | 'open' | 'half_open';
  company: string;
  baseUrl: string;
  lastModified: {
    customers: string | null;
    salesOrders: string | null;
    inventory: string | null;
  };
  errorMessage: string | null;
  checkedAt: string;
}

export interface Warehouse {
  spireId: number;
  code: string;
  description: string | null;
  status: 'active' | 'inactive';
  modifiedAt: string | null;
}

export interface ActivityEvent {
  id: string;
  userId: string | null;
  kind: string;
  summary: string;
  payload: Record<string, unknown>;
  occurredAt: string;
}

export type UserRole = 'admin' | 'dispatcher' | 'office' | 'field_tech';
export type UserTheme = 'light' | 'dark';
export type UserDensity = 'comfortable' | 'compact';

export interface AppUser {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  defaultWarehouse: string | null;
  /** Tech.id this user IS on the dispatch roster, or null. */
  techId: string | null;
  /** Spire warehouse code of the linked tech's truck (from /me only). */
  truckWarehouse?: string | null;
  bookmarkedParts: string[];
  theme: UserTheme | null;
  density: UserDensity | null;
  active: boolean;
  createdAt: string;
  modifiedAt: string;
}

// ─── Field time-tracking (mirror apps/api/src/modules/field/) ──────────────
export type FieldPhase =
  | 'scheduled'
  | 'traveling'
  | 'on_site'
  | 'work_in_progress'
  | 'paused'
  | 'work_completed'
  | 'signed_off';

export interface TimeEntry {
  id: string;
  userId: string;
  spireSalesOrderId: number;
  spireOrderNo: string;
  phase: FieldPhase;
  startedAt: string;
  endedAt: string | null;
  durationSec: number | null;
  note: string | null;
}

export interface ActiveJob {
  activeJobSpireId: number;
  activeJobOrderNo: string;
  currentPhase: FieldPhase;
  openEntry: TimeEntry;
}

export interface JobLockedConflict {
  code: 'JOB_ALREADY_ACTIVE';
  activeJobSpireId: number;
  activeJobOrderNo: string;
}

export interface CreateUserBody {
  email: string;
  displayName: string;
  role: UserRole;
  defaultWarehouse?: string | null;
  techId?: string | null;
  /** Optional initial password. If omitted, Cognito auto-generates and emails. */
  password?: string;
  /** Default true. False = password is permanent, no first-login prompt. */
  forcePasswordChange?: boolean;
  /** Default true. False = suppress Cognito's invitation email. */
  sendInviteEmail?: boolean;
}

export interface CreatedUser extends AppUser {
  /** Echoed back when the admin supplied a password — display once. Null when Cognito auto-generated and emailed. */
  passwordHandoff: string | null;
}

export interface ResetPasswordBody {
  password: string;
  /** True = user can log in immediately. False = forced change on next login. */
  permanent: boolean;
}

export interface UpdateUserBody {
  displayName?: string;
  role?: UserRole;
  defaultWarehouse?: string | null;
  techId?: string | null;
  active?: boolean;
}

/** A sales-type key the tech is skilled in (Admin → Sales types). */
export type TechTrade = string;
export type TechAvatar = 'service' | 'install' | 'pm' | 'emergency' | 'sales';

export interface Tech {
  id: string;
  name: string;
  initials: string;
  avatar: TechAvatar;
  trades: TechTrade[];
  active: boolean;
  /** Days of the week the tech normally works, 0 = Sunday .. 6 = Saturday. */
  workDays: number[];
  /** Spire warehouse code of the tech's truck/van, or null. */
  warehouseCode: string | null;
  createdAt: string;
  modifiedAt: string;
}

// ─── Trucks (van inventory) ──────────────────────────────────────────────

export interface TruckSummary {
  techId: string;
  techName: string;
  whse: string;
  whseDescription: string | null;
}

export interface TruckStockLine {
  inventoryId: number;
  partNo: string;
  description: string;
  whse: string;
  onHand: string;
  committed: string;
  available: string;
  uom: string | null;
}

export interface StockMoveLine {
  spireId: number;
  inventoryId: number;
  partNo: string;
  whse: string;
  description: string | null;
  qty: string;
  cost: string | null;
  uom: string | null;
}

export interface StockMove {
  spireId: number;
  kind: 'transfer' | 'adjustment';
  number: string;
  date: string;
  referenceNo: string | null;
  sourceWhse: string | null;
  destinationWhse: string | null;
  posted: boolean;
  transactionNo: string | null;
  createdBy: string | null;
  created: string | null;
  items: StockMoveLine[];
}

export interface CreateTransferBody {
  sourceWhse: string;
  destinationWhse: string;
  referenceNo?: string;
  items: Array<{ partNo: string; qty: string; uom?: string }>;
}

export type TruckCountStatus = 'submitted' | 'approved' | 'rejected';

export interface TruckCountLine {
  id: string;
  partNo: string;
  description: string | null;
  uom: string | null;
  expectedQty: string;
  countedQty: string;
  variance: string;
}

export interface TruckCount {
  id: string;
  techId: string;
  techName: string;
  whse: string;
  status: TruckCountStatus;
  note: string | null;
  submittedBy: string;
  submittedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  spireAdjustmentId: number | null;
  spireAdjustmentNo: string | null;
  lineCount: number;
  varianceCount: number;
  lines: TruckCountLine[];
}

export interface TechTimeOff {
  id: string;
  techId: string;
  /** YYYY-MM-DD inclusive. */
  startDate: string;
  /** YYYY-MM-DD inclusive. */
  endDate: string;
  /** "HH:MM" window applied on each day of the range; both null = whole day. */
  startTime: string | null;
  endTime: string | null;
  reason: string | null;
}

/** An extra visit on a job (multi-day work / a return trip). The order's own fop_* UDFs are the first visit. */
export interface JobVisit {
  id: string;
  spireSalesOrderId: number;
  spireOrderNo: string;
  /** Tech leading this visit — may differ from the order's lead. */
  techId: string;
  helperIds: string[];
  /** YYYY-MM-DD. */
  date: string;
  /** "HH:MM". */
  startTime: string;
  durationHours: number;
  note: string | null;
}

export interface CreateJobVisitBody {
  spireSalesOrderId: number;
  techId: string;
  helperIds?: string[];
  date: string;
  startTime: string;
  durationHours: number;
  note?: string;
  /** One visit per day through this date, skipping the tech's days off and company holidays. */
  untilDate?: string;
}

export type UpdateJobVisitBody = Partial<Pick<JobVisit, 'techId' | 'helperIds' | 'date' | 'startTime' | 'durationHours'>> & {
  note?: string;
};

export interface CompanyHoliday {
  /** YYYY-MM-DD. */
  date: string;
  name: string;
}

export interface CreateTechBody {
  name: string;
  initials?: string;
  avatar: TechAvatar;
  trades: TechTrade[];
  workDays?: number[];
  warehouseCode?: string | null;
}

export interface UpdateTechBody {
  name?: string;
  initials?: string;
  avatar?: TechAvatar;
  trades?: TechTrade[];
  active?: boolean;
  workDays?: number[];
  warehouseCode?: string | null;
}

export const api = {
  inventory: {
    search: (params: InventorySearchParams, signal?: AbortSignal) =>
      request<InventoryItemSummary[]>('GET', '/v1/inventory', { params: { ...params }, signal }),
    getById: (id: number, signal?: AbortSignal) =>
      request<InventoryItem>('GET', `/v1/inventory/${id}`, { signal }),
  },
  customers: {
    search: (params: CustomerSearchParams, signal?: AbortSignal) =>
      request<CustomerSummary[]>('GET', '/v1/customers', {
        params: {
          q: params.q,
          customerNo: params.customerNo,
          customerNoExact: params.customerNoExact,
          status: params.status,
          limit: params.limit,
          hold: params.hold === undefined ? undefined : String(params.hold),
        },
        signal,
      }),
    getById: (id: number, signal?: AbortSignal) =>
      request<Customer>('GET', `/v1/customers/${id}`, { signal }),
  },
  salesOrders: {
    search: (params: SalesOrderSearchParams, signal?: AbortSignal) =>
      request<SalesOrderSummary[]>('GET', '/v1/sales-orders', {
        params: {
          status: params.status,
          type: params.type,
          customerNo: params.customerNo,
          orderNo: params.orderNo,
          assignedToTechId: params.assignedToTechId,
          limit: params.limit,
          // request.ts drops undefined/null/'' params, so omit when not set.
          includeUdf: params.includeUdf ? 'true' : undefined,
        },
        signal,
      }),
    getById: (id: number, signal?: AbortSignal) =>
      request<SalesOrder>('GET', `/v1/sales-orders/${id}`, { signal }),
    create: (body: CreateSalesOrderBody, idempotencyKey: string, signal?: AbortSignal) =>
      request<SalesOrder>('POST', '/v1/sales-orders', {
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
        signal,
      }),
    update: (
      spireId: number,
      body: UpdateSalesOrderBody,
      idempotencyKey?: string,
      signal?: AbortSignal,
    ) =>
      request<SalesOrder>('PUT', `/v1/sales-orders/${spireId}`, {
        body,
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        signal,
      }),
    addItem: (
      spireId: number,
      body: AddLineBody,
      idempotencyKey?: string,
      signal?: AbortSignal,
    ) =>
      request<AddLineResponse>('POST', `/v1/sales-orders/${spireId}/items`, {
        body,
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        signal,
      }),
    attachSignature: (
      spireId: number,
      body: AttachSignatureBody,
      signal?: AbortSignal,
    ) =>
      request<AttachSignatureResponse>(
        'POST',
        `/v1/sales-orders/${spireId}/signature`,
        { body, signal },
      ),
    addPhoto: (spireId: number, body: AddPhotoBody, signal?: AbortSignal) =>
      request<AddPhotoResponse>('POST', `/v1/sales-orders/${spireId}/photos`, {
        body,
        signal,
      }),
    addNote: (
      spireId: number,
      body: { body: string; subject?: string },
      signal?: AbortSignal,
    ) =>
      request<{ orderNo: string; noteId: number }>(
        'POST',
        `/v1/sales-orders/${spireId}/notes`,
        { body, signal },
      ),
    listNotes: (spireId: number, signal?: AbortSignal) =>
      request<JobNote[]>('GET', `/v1/sales-orders/${spireId}/notes`, { signal }),
    listPhotos: (spireId: number, signal?: AbortSignal) =>
      request<OrderPhoto[]>('GET', `/v1/sales-orders/${spireId}/photos`, { signal }),
  },
  attachments: {
    list: (spireId: number, signal?: AbortSignal) =>
      request<JobAttachment[]>('GET', `/v1/sales-orders/${spireId}/attachments`, { signal }),
    upload: (spireId: number, body: UploadJobAttachmentBody, signal?: AbortSignal) =>
      request<JobAttachment>('POST', `/v1/sales-orders/${spireId}/attachments`, { body, signal }),
    remove: (id: string, signal?: AbortSignal) =>
      request<void>('DELETE', `/v1/attachments/${id}`, { signal }),
  },
  pricing: {
    resolve: (body: ResolvePricingBody, signal?: AbortSignal) =>
      request<ResolvePricingResponse>('POST', '/v1/pricing/resolve', { body, signal }),
  },
  salesTypes: {
    list: (signal?: AbortSignal) => request<SalesType[]>('GET', '/v1/sales-types', { signal }),
    create: (body: CreateSalesTypeBody, signal?: AbortSignal) =>
      request<SalesType>('POST', '/v1/sales-types', { body, signal }),
    update: (key: string, body: UpdateSalesTypeBody, signal?: AbortSignal) =>
      request<SalesType>('PATCH', `/v1/sales-types/${encodeURIComponent(key)}`, { body, signal }),
  },
  settings: {
    scheduling: (signal?: AbortSignal) =>
      request<SchedulingSettings>('GET', '/v1/settings/scheduling', { signal }),
    updateScheduling: (body: Partial<SchedulingSettings>, signal?: AbortSignal) =>
      request<SchedulingSettings>('PUT', '/v1/settings/scheduling', { body, signal }),
    phaseMap: (signal?: AbortSignal) => request<PhaseMap>('GET', '/v1/settings/phase-map', { signal }),
    updatePhaseMap: (body: Partial<PhaseMap>, signal?: AbortSignal) =>
      request<PhaseMap>('PUT', '/v1/settings/phase-map', { body, signal }),
    spirePhases: (signal?: AbortSignal) => request<SpirePhase[]>('GET', '/v1/settings/spire-phases', { signal }),
    fieldJobs: (signal?: AbortSignal) => request<FieldJobsSettings>('GET', '/v1/settings/field-jobs', { signal }),
    updateFieldJobs: (body: Partial<Omit<FieldJobsSettings, 'billToCustomerName'>>, signal?: AbortSignal) =>
      request<FieldJobsSettings>('PUT', '/v1/settings/field-jobs', { body, signal }),
  },
  admin: {
    spireHealth: (signal?: AbortSignal) =>
      request<SpireHealth>('GET', '/v1/admin/spire-health', { signal }),
    warehouses: (signal?: AbortSignal) =>
      request<Warehouse[]>('GET', '/v1/admin/warehouses', { signal }),
    users: {
      list: (includeInactive: boolean, signal?: AbortSignal) =>
        request<AppUser[]>('GET', '/v1/admin/users', {
          params: { includeInactive: includeInactive ? 'true' : undefined },
          signal,
        }),
      create: (body: CreateUserBody, signal?: AbortSignal) =>
        request<CreatedUser>('POST', '/v1/admin/users', { body, signal }),
      update: (id: string, body: UpdateUserBody, signal?: AbortSignal) =>
        request<AppUser>('PATCH', `/v1/admin/users/${id}`, { body, signal }),
      deactivate: (id: string, signal?: AbortSignal) =>
        request<AppUser>('DELETE', `/v1/admin/users/${id}`, { signal }),
      resetPassword: (id: string, body: ResetPasswordBody, signal?: AbortSignal) =>
        request<void>('POST', `/v1/admin/users/${id}/password`, { body, signal }),
    },
    techs: {
      list: (includeInactive: boolean, signal?: AbortSignal) =>
        request<Tech[]>('GET', '/v1/admin/techs', {
          params: { includeInactive: includeInactive ? 'true' : undefined },
          signal,
        }),
      /** Id → name for every tech (inactive included). Readable by every role, unlike `list`. */
      names: (signal?: AbortSignal) =>
        request<Array<{ id: string; name: string; initials: string }>>('GET', '/v1/admin/techs/names', { signal }),
      create: (body: CreateTechBody, signal?: AbortSignal) =>
        request<Tech>('POST', '/v1/admin/techs', { body, signal }),
      update: (id: string, body: UpdateTechBody, signal?: AbortSignal) =>
        request<Tech>('PATCH', `/v1/admin/techs/${id}`, { body, signal }),
      deactivate: (id: string, signal?: AbortSignal) =>
        request<Tech>('DELETE', `/v1/admin/techs/${id}`, { signal }),
      timeOff: (params: { techId?: string; from?: string; to?: string }, signal?: AbortSignal) =>
        request<TechTimeOff[]>('GET', '/v1/admin/techs/time-off', { params: { ...params }, signal }),
      addTimeOff: (techId: string, body: { startDate: string; endDate: string; startTime?: string; endTime?: string; reason?: string }, signal?: AbortSignal) =>
        request<TechTimeOff>('POST', `/v1/admin/techs/${techId}/time-off`, { body, signal }),
      removeTimeOff: (entryId: string, signal?: AbortSignal) =>
        request<void>('DELETE', `/v1/admin/techs/time-off/${entryId}`, { signal }),
      holidays: (params: { from?: string; to?: string } = {}, signal?: AbortSignal) =>
        request<CompanyHoliday[]>('GET', '/v1/admin/techs/holidays', { params: { ...params }, signal }),
      setHoliday: (body: CompanyHoliday, signal?: AbortSignal) =>
        request<CompanyHoliday>('POST', '/v1/admin/techs/holidays', { body, signal }),
      removeHoliday: (date: string, signal?: AbortSignal) =>
        request<void>('DELETE', `/v1/admin/techs/holidays/${date}`, { signal }),
    },
  },
  auth: {
    /** Lightweight list of users available for the dev login picker. */
    candidates: (signal?: AbortSignal) =>
      request<Array<{ id: string; email: string; displayName: string; role: UserRole }>>(
        'GET',
        '/v1/dev-auth/users',
        { signal },
      ),
    login: (email: string, signal?: AbortSignal) =>
      request<{ accessToken: string; expiresIn: number; user: AppUser }>(
        'POST',
        '/v1/dev-auth/login',
        { body: { email }, signal },
      ),
  },
  visits: {
    list: (params: { from?: string; to?: string; techId?: string; orderId?: number }, signal?: AbortSignal) =>
      request<JobVisit[]>('GET', '/v1/job-visits', {
        params: { from: params.from, to: params.to, techId: params.techId, orderId: params.orderId?.toString() },
        signal,
      }),
    create: (body: CreateJobVisitBody, signal?: AbortSignal) =>
      request<JobVisit[]>('POST', '/v1/job-visits', { body, signal }),
    update: (id: string, body: UpdateJobVisitBody, signal?: AbortSignal) =>
      request<JobVisit>('PATCH', `/v1/job-visits/${id}`, { body, signal }),
    remove: (id: string, signal?: AbortSignal) => request<void>('DELETE', `/v1/job-visits/${id}`, { signal }),
  },
  trucks: {
    list: (signal?: AbortSignal) => request<TruckSummary[]>('GET', '/v1/trucks', { signal }),
    warehouses: (signal?: AbortSignal) =>
      request<Array<Pick<Warehouse, 'code' | 'description' | 'status'>>>('GET', '/v1/trucks/warehouses', { signal }),
    mine: (q?: string, signal?: AbortSignal) =>
      request<{ truck: TruckSummary; stock: TruckStockLine[] }>('GET', '/v1/trucks/mine', {
        params: { q: q || undefined },
        signal,
      }),
    stock: (whse: string, q?: string, signal?: AbortSignal) =>
      request<TruckStockLine[]>('GET', `/v1/trucks/${encodeURIComponent(whse)}/stock`, {
        params: { q: q || undefined },
        signal,
      }),
    transfers: (whse: string, signal?: AbortSignal) =>
      request<StockMove[]>('GET', `/v1/trucks/${encodeURIComponent(whse)}/transfers`, { signal }),
    /** Creates AND posts the transfer in Spire. Idempotent per key. */
    transfer: (body: CreateTransferBody, idempotencyKey: string, signal?: AbortSignal) =>
      request<StockMove>('POST', '/v1/trucks/transfers', {
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
        signal,
      }),
    counts: (status?: TruckCountStatus, signal?: AbortSignal) =>
      request<TruckCount[]>('GET', '/v1/trucks/counts', { params: { status }, signal }),
    count: (id: string, signal?: AbortSignal) =>
      request<TruckCount>('GET', `/v1/trucks/counts/${id}`, { signal }),
    submitCount: (body: { note?: string; lines: Array<{ partNo: string; countedQty: string }> }, signal?: AbortSignal) =>
      request<TruckCount>('POST', '/v1/trucks/counts', { body, signal }),
    approveCount: (id: string, note?: string, signal?: AbortSignal) =>
      request<TruckCount>('POST', `/v1/trucks/counts/${id}/approve`, { body: note ? { note } : {}, signal }),
    rejectCount: (id: string, note?: string, signal?: AbortSignal) =>
      request<TruckCount>('POST', `/v1/trucks/counts/${id}/reject`, { body: note ? { note } : {}, signal }),
  },
  field: {
    /** Office-side phase set (Operations side panel). Never touches a tech's time entries. */
    officeSetPhase: (spireId: number, phase: FieldPhase, signal?: AbortSignal) =>
      request<OfficePhaseResult>('POST', `/v1/sales-orders/${spireId}/phase`, { body: { phase }, signal }),
    /** Returns the caller's currently-open time entry, or null. */
    active: (signal?: AbortSignal) =>
      request<ActiveJob | null>('GET', '/v1/field/active', { signal }),
    /** Start a new time entry on a job. Default phase = 'traveling'. */
    start: (
      body: { spireSalesOrderId: number; phase?: FieldPhase },
      signal?: AbortSignal,
    ) => request<TimeEntry>('POST', '/v1/field/time-entries/start', { body, signal }),
    /** Close the open entry and open a new one with the supplied phase. */
    phaseChange: (
      body: { phase: FieldPhase; note?: string },
      signal?: AbortSignal,
    ) =>
      request<{ previousEntry: TimeEntry | null; newEntry: TimeEntry }>(
        'POST',
        '/v1/field/time-entries/phase-change',
        { body, signal },
      ),
    /** Close the open entry and clear the active-job lock. */
    stop: (
      body: { note?: string; finalPhase?: FieldPhase },
      signal?: AbortSignal,
    ) =>
      request<{ entries: TimeEntry[]; totalSec: number }>(
        'POST',
        '/v1/field/time-entries/stop',
        { body, signal },
      ),
    /** Office-side read of all entries against a sales order. */
    entriesForOrder: (spireSalesOrderId: number, signal?: AbortSignal) =>
      request<TimeEntry[]>(
        'GET',
        `/v1/sales-orders/${spireSalesOrderId}/time-entries`,
        { signal },
      ),
  },
  activity: {
    /** Platform-wide recent activity (office roles only), newest first. */
    recent: (limit?: number, signal?: AbortSignal) =>
      request<ActivityEvent[]>('GET', '/v1/activity', {
        params: { limit: limit ? String(limit) : undefined },
        signal,
      }),
  },
  me: {
    get: (signal?: AbortSignal) => request<AppUser>('GET', '/v1/me', { signal }),
    update: (
      body: {
        displayName?: string;
        defaultWarehouse?: string | null;
        bookmarkedParts?: string[];
        theme?: UserTheme | null;
        density?: UserDensity | null;
      },
      signal?: AbortSignal,
    ) => request<AppUser>('PATCH', '/v1/me', { body, signal }),
    activity: (limit?: number, signal?: AbortSignal) =>
      request<ActivityEvent[]>('GET', '/v1/me/activity', {
        params: { limit: limit ? String(limit) : undefined },
        signal,
      }),
  },
  health: {
    liveness: (signal?: AbortSignal) =>
      request<{ status: string; uptimeSec: number }>('GET', '/healthz', {
        signal,
        absolute: true,
      }),
  },
};

// ─── Internals ────────────────────────────────────────────────────────────

interface RequestOptions {
  params?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  /** Hit BASE root (no /v1 prefix). Used for /healthz, /readyz. */
  absolute?: boolean;
  /** JSON-serializable body for POST/PUT. */
  body?: unknown;
  /** Additional request headers (e.g. Idempotency-Key). */
  headers?: Record<string, string>;
  /** Internal: set on the single post-refresh retry of a 401'd request. */
  _retriedAfterRefresh?: boolean;
}

async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const url = buildUrl(path, options);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers ?? {}),
  };

  // Attach Bearer for everything except the public auth endpoints. Skip
  // when there's no token (the request will 401 if the API needs auth,
  // which the 401-handler below catches).
  if (!isPublicPath(path)) {
    const token = currentAccessToken();
    if (token && !headers.Authorization) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials: 'include',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (err) {
    throw new ApiError(0, 'network', err instanceof Error ? err.message : 'Network failure', true);
  }

  const text = await response.text();
  const body: unknown = text.length ? safeJsonParse(text) : null;

  if (!response.ok) {
    // 401 on a non-login request: our token is stale or gone. Before
    // declaring the session dead, attempt ONE silent Cognito refresh and
    // replay the request (idempotent-safe: same method/body, and the
    // original attempt was rejected before doing work). Spire-origin auth
    // failures are explicitly NOT our session's problem — the API maps
    // them to 502 now, but guard on kind for older servers too.
    if (response.status === 401 && !isPublicPath(path)) {
      // kind 'auth' = Spire rejected the SERVICE credentials (older API
      // versions returned that as 401; current maps it to 502). Either
      // way it must not nuke the user's Tinsmith session.
      const earlyKind = (body as { error?: { kind?: string } } | null)?.error?.kind ?? null;
      if (earlyKind !== 'auth') {
        if (!options._retriedAfterRefresh) {
          const restored = await attemptSessionRestore();
          if (restored) {
            return request<T>(method, path, { ...options, _retriedAfterRefresh: true });
          }
        }
        useAuth.getState().clear();
      }
    }
    const envelope = body as { error?: { kind?: string; message?: string; retryable?: boolean } };
    const kind = envelope?.error?.kind ?? null;
    const message =
      envelope?.error?.message ?? (typeof body === 'string' ? body : `HTTP ${response.status}`);
    const retryable = envelope?.error?.retryable ?? response.status >= 500;
    throw new ApiError(response.status, kind, message, retryable, body);
  }
  return body as T;
}

function buildUrl(path: string, options: RequestOptions): string {
  const base = options.absolute ? stripTrailingSlash(BASE).replace(/\/api$/, '') : BASE;
  const url = new URL(joinPath(base, path), window.location.origin);
  if (options.params) {
    for (const [key, value] of Object.entries(options.params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

function joinPath(base: string, path: string): string {
  return `${stripTrailingSlash(base)}/${path.replace(/^\//, '')}`;
}

function stripTrailingSlash(s: string): string {
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
